/**
 * Predictive Search
 * Fetches and displays search suggestions as the user types
 */
(function() {
  'use strict';

  let searchTimeout;
  const DEBOUNCE_DELAY = 300;
  const MIN_QUERY_LENGTH = 2;

  /**
   * Initialize predictive search for a form
   */
  function initPredictiveSearch(form) {
    const input = form.querySelector('[data-predictive-search-input]');
    if (!input) return;

    const resultsContainer = form.querySelector('.predictive-search');
    if (!resultsContainer) return;

    // Hide results on form submit
    form.addEventListener('submit', function() {
      hideResults(resultsContainer);
    });

    // Handle input changes
    input.addEventListener('input', function(e) {
      const query = e.target.value.trim();
      
      if (query.length < MIN_QUERY_LENGTH) {
        hideResults(resultsContainer);
        return;
      }

      // Debounce the search
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(function() {
        fetchSearchSuggestions(query, resultsContainer);
      }, DEBOUNCE_DELAY);
    });

    // Hide results when clicking outside
    document.addEventListener('click', function(e) {
      if (!form.contains(e.target)) {
        hideResults(resultsContainer);
      }
    });

    // Handle keyboard navigation
    input.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        hideResults(resultsContainer);
        input.blur();
      }
    });
  }

  /**
   * Fetch search suggestions from Shopify API
   */
  function fetchSearchSuggestions(query, container) {
    const searchUrl = '/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=5';
    
    container.classList.add('predictive-search--loading');
    container.innerHTML = '';

    fetch(searchUrl)
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Search request failed');
        }
        return response.json();
      })
      .then(function(data) {
        // Log the data structure for debugging (remove in production)
        if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
          console.log('Predictive search API response:', data);
        }
        renderResults(data, container, query);
      })
      .catch(function(error) {
        console.error('Predictive search error:', error);
        hideResults(container);
      })
      .finally(function() {
        container.classList.remove('predictive-search--loading');
      });
  }

  /**
   * Render search results
   */
  function renderResults(data, container, query) {
    // Shopify's suggest.json returns data in resources.results.products format
    const products = data.resources && data.resources.results && data.resources.results.products 
      ? data.resources.results.products 
      : [];
    
    if (products.length === 0) {
      hideResults(container);
      return;
    }

    let html = '<div class="predictive-search__results">';
    html += '<ul class="predictive-search__results-list">';

    products.forEach(function(product) {
      // Shopify suggest.json API returns images in 'thumbnail' property
      // It can be a string URL or an object with src/url properties
      let imageUrl = '';
      
      if (product.thumbnail) {
        if (typeof product.thumbnail === 'string') {
          imageUrl = product.thumbnail;
        } else if (product.thumbnail.src) {
          imageUrl = product.thumbnail.src;
        } else if (product.thumbnail.url) {
          imageUrl = product.thumbnail.url;
        }
      } else if (product.featured_image) {
        if (typeof product.featured_image === 'string') {
          imageUrl = product.featured_image;
        } else if (product.featured_image.src) {
          imageUrl = product.featured_image.src;
        } else if (product.featured_image.url) {
          imageUrl = product.featured_image.url;
        }
      } else if (product.image) {
        if (typeof product.image === 'string') {
          imageUrl = product.image;
        } else if (product.image.src) {
          imageUrl = product.image.src;
        } else if (product.image.url) {
          imageUrl = product.image.url;
        }
      }
      
      const image = imageUrl 
        ? '<img src="' + escapeHtml(imageUrl) + '" alt="' + escapeHtml(product.title) + '" class="predictive-search__item-image" loading="lazy" width="60" height="60">'
        : '';
      
      html += '<li class="predictive-search__item">';
      html += '<a href="' + escapeHtml(product.url) + '" class="predictive-search__item-link">';
      if (image) {
        html += '<div class="predictive-search__item-image-wrapper">' + image + '</div>';
      }
      html += '<div class="predictive-search__item-content">';
      html += '<div class="predictive-search__item-title">' + escapeHtml(product.title) + '</div>';
      if (product.price) {
        html += '<div class="predictive-search__item-price">' + escapeHtml(product.price) + '</div>';
      }
      html += '</div>';
      html += '</a>';
      html += '</li>';
    });

    html += '</ul>';
    html += '<div class="predictive-search__footer">';
    html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="predictive-search__view-all">View all results</a>';
    html += '</div>';
    html += '</div>';

    container.innerHTML = html;
    container.setAttribute('aria-hidden', 'false');
    container.style.display = 'block';
  }

  /**
   * Hide search results
   */
  function hideResults(container) {
    if (container) {
      container.innerHTML = '';
      container.setAttribute('aria-hidden', 'true');
      container.style.display = 'none';
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Initialize all predictive search forms on page load
   */
  function init() {
    const forms = document.querySelectorAll('[data-predictive-search-form]');
    forms.forEach(function(form) {
      initPredictiveSearch(form);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize for dynamically added forms (Shopify theme editor)
  if (typeof document.addEventListener !== 'undefined') {
    document.addEventListener('shopify:section:load', init);
  }
})();

